import { z } from 'zod';

import type {
  AgentSession,
  AgentSetup,
  CodingAgentId,
  HooksState,
  InstallStep,
} from '@littlebird/agent-hooks';

export const CODING_AGENTS_GET_STATE_CHANNEL = 'coding-agents:get-state';
export const CODING_AGENTS_SET_CONNECTED_CHANNEL =
  'coding-agents:set-connected';
export const CODING_AGENTS_LIST_SESSIONS_CHANNEL =
  'coding-agents:list-sessions';
export const CODING_AGENTS_SESSION_CHANGED_CHANNEL =
  'coding-agents:session-changed';
export const CODING_AGENTS_LAUNCH_CHANNEL = 'coding-agents:launch';
export const CODING_AGENTS_SEND_MESSAGE_CHANNEL = 'coding-agents:send-message';
export const CODING_AGENTS_STOP_CHANNEL = 'coding-agents:stop';
export const CODING_AGENTS_OPEN_SESSION_CHANNEL = 'coding-agents:open-session';
export const CODING_AGENTS_INSTALL_CHANNEL = 'coding-agents:install';
export const CODING_AGENTS_SIGN_IN_CHANNEL = 'coding-agents:sign-in';
export const CODING_AGENTS_REOPEN_SIGN_IN_CHANNEL =
  'coding-agents:reopen-sign-in';
export const CODING_AGENTS_CANCEL_SETUP_CHANNEL = 'coding-agents:cancel-setup';
/** Main to renderer: an install has started one of its parts */
export const CODING_AGENTS_INSTALL_STEP_CHANNEL = 'coding-agents:install-step';

export interface InstallStepEvent {
  id: CodingAgentId;
  step: InstallStep;
}

/** A setup step under way for an agent. An agent has at most one at a time */
export type SetupActivity = 'installing' | 'signing-in';

export interface CodingAgentState extends HooksState {
  id: CodingAgentId;
  label: string;
  setup: AgentSetup;
  /** Whether a sign-in's page can be opened again. Devin opens its page without naming it */
  canReopenSignIn: boolean;
}

const agentId = z.enum([
  'claude-code',
  'codex',
  'cursor',
  'devin',
]) satisfies z.ZodType<CodingAgentId>;

// An id goes to a command line and names a folder, so it starts with a letter or a digit
const sessionId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,199}$/);

export const AgentRefSchema = z.object({ id: agentId });

export const SetConnectedSchema = AgentRefSchema.extend({
  connected: z.boolean(),
});

// The limits are the ones the backend puts on a coding agent card
export const AgentLaunchRequestSchema = z.object({
  agent: agentId,
  cwd: z.string().min(1).max(1_000),
  prompt: z.string().min(1).max(20_000),
  access: z.enum(['full', 'edit']),
  resumeSessionId: sessionId.optional(),
  /** The action card the run starts from. Main reports the run under it */
  cardId: z.string().uuid().optional(),
  /** Start even though another agent is working in the folder */
  startAnyway: z.boolean().optional(),
  /** Trust the folder in the agent first. Only the user's own choice on the card sets this */
  trustFolder: z.boolean().optional(),
});

export const AgentSessionRefSchema = z.object({ agent: agentId, sessionId });

// The card's folder, for an agent whose app opens a session by its folder
export const OpenSessionRequestSchema = AgentSessionRefSchema.extend({
  cwd: z.string().min(1).max(1_000),
});

export const AgentMessageSchema = AgentSessionRefSchema.extend({
  text: z.string().trim().min(1).max(20_000),
});

export type AgentLaunchRequest = z.infer<typeof AgentLaunchRequestSchema>;
export type AgentSessionRef = z.infer<typeof AgentSessionRefSchema>;
export type OpenSessionRequest = z.infer<typeof OpenSessionRequestSchema>;
export type AgentMessage = z.infer<typeof AgentMessageSchema>;

/** How an action on a coding agent went. `error` is written for the user */
export type AgentActionResult<T = unknown> =
  | ({ success: true } & T)
  // `busy` says another agent is working in the folder, and the user may start anyway. `untrustedFolder` says the
  // agent refused a folder the user has not trusted in it, and the user may trust it
  | { success: false; error: string; busy?: true; untrustedFolder?: true };

/** How a setup step ended. A failed install also carries the command that installs the agent from a terminal */
export type AgentSetupResult =
  | { success: true; state: CodingAgentState }
  | { success: false; error: string; installCommand?: string };

export type { AgentSession, CodingAgentId, InstallStep };
